import Layout from 'components/UI/Layout';
import BreadcrumbNav from 'components/UI/BreadcrumbNav';
import React, { ReactElement } from 'react';
import { BrandedSiteProps, PageDates } from 'lib/routing';
import {
  PreparedComponentObject,
  DynamicComponentTypes,
  buildDynamicComponent,
  SerializableOtherParams,
} from 'components';
import { formatCityName } from 'lib/util';
import type { BreadcrumbItem } from 'lib/cms/breadcrumbs';
import { JsonObject } from 'lib/json';

export type PageProps<K extends DynamicComponentTypes = DynamicComponentTypes, CP = unknown> = {
  pageType: 'page';
  preview: boolean;
  content: PreparedComponentObject<K, CP>[];
  siteData: BrandedSiteProps;
  currentSlug: string;
  title: string;
  description: string;
  noIndexNoFollow: boolean;
  pageDates?: PageDates;
  hideTopNavCtas?: boolean;
  otherParams?: SerializableOtherParams | undefined;
  breadcrumbs: BreadcrumbItem[];
  jsonSchema?: JsonObject;
};

export default function PageLayout<K extends DynamicComponentTypes, CP>({
  preview,
  title,
  description,
  currentSlug,
  content,
  siteData,
  noIndexNoFollow,
  pageDates,
  hideTopNavCtas,
  otherParams,
  breadcrumbs,
  jsonSchema,
}: PageProps<K, CP>): ReactElement {
  const slugArray: string | string[] = otherParams?.query.slug || [];
  let hubObj: { label: string } | undefined;
  if (
    slugArray.length === 2 &&
    ['optometrists', 'ophthalmologists'].includes(slugArray[0].toLowerCase())
  ) {
    hubObj = {
      label: formatCityName(slugArray[1] as string),
    };
  }
  const components = content.map((comp: PreparedComponentObject<K, CP>, index: number) => {
    const dynamicComp = buildDynamicComponent(comp, siteData.siteName, preview, { hub: hubObj });
    if (dynamicComp === undefined) return;

    return <div key={index}>{dynamicComp}</div>;
  });

  const nextSeoProps = {
    description,
    title,
  };

  return (
    <Layout
      noIndexNoFollow={noIndexNoFollow}
      siteData={siteData}
      preview={preview}
      currentSlug={currentSlug}
      nextSeoProps={nextSeoProps}
      pageDates={pageDates}
      hideTopNavCtas={hideTopNavCtas}
      jsonSchema={jsonSchema}
    >
      {breadcrumbs.length > 0 && <BreadcrumbNav items={breadcrumbs} />}
      <div>{components}</div>
    </Layout>
  );
}
